import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { apiErrorMessage } from '../core/api-error';
import { API_CLIENT_CONFIG } from '../core/api-client-config';
import { PaginationResult } from '../core/pagination';
import {
  AddContactHistoryRequest,
  Contact,
  ContactStatus,
  ContactUserSuggestion,
  ContactWithHistory,
  CreateContactRequest,
  LinkContactUserRequest,
  UpdateContactRequest,
} from '../models';

@Injectable({ providedIn: 'root' })
export class AdminContactsApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = `${inject(API_CLIENT_CONFIG).crmApiBaseUrl}/api/admin/BackofficeContacts`;

  getContacts(options?: {
    search?: string;
    contactSourceId?: string;
    referrerId?: string;
    status?: ContactStatus;
    isRegistered?: boolean;
    skip?: number;
    take?: number;
  }) {
    let params = new HttpParams();

    if (options?.search) {
      params = params.append('search', options.search);
    }

    if (options?.contactSourceId) {
      params = params.append('contactSourceId', options.contactSourceId);
    }

    if (options?.referrerId) {
      params = params.append('referrerId', options.referrerId);
    }

    if (options?.status) {
      params = params.append('status', options.status);
    }

    if (options?.isRegistered !== null && options?.isRegistered !== undefined) {
      params = params.append('isRegistered', options.isRegistered);
    }

    if (options?.skip !== null && options?.skip !== undefined) {
      params = params.append('skip', options.skip);
    }

    if (options?.take !== null && options?.take !== undefined) {
      params = params.append('take', options.take);
    }

    return this.httpClient.get<PaginationResult<Contact>>(`${this.baseUrl}`, {
      params,
    });
  }

  getContact(id: string) {
    return this.httpClient.get<ContactWithHistory>(`${this.baseUrl}/${id}`);
  }

  createContact(dto: CreateContactRequest) {
    return this.httpClient.post<{ id: string }>(`${this.baseUrl}`, dto).pipe(
      catchError((error) =>
        throwError(() =>
          apiErrorMessage(error, {
            AlreadyExists:
              "L'indirizzo email è già in uso da un altro contatto.",
            NotFound:
              'La fonte o il referente indicati non sono più disponibili.',
          }),
        ),
      ),
    );
  }

  updateContact(dto: UpdateContactRequest) {
    return this.httpClient.put<void>(`${this.baseUrl}`, dto).pipe(
      catchError((error) =>
        throwError(() =>
          apiErrorMessage(error, {
            AlreadyExists:
              "L'indirizzo email è già in uso da un altro contatto.",
            NotFound:
              'Il contatto, la fonte o il referente indicati non sono più disponibili.',
          }),
        ),
      ),
    );
  }

  deleteContact(id: string) {
    return this.httpClient
      .delete<void>(`${this.baseUrl}/${id}`)
      .pipe(
        catchError((error) =>
          throwError(() =>
            apiErrorMessage(
              error,
              { NotFound: 'Il contatto non esiste più.' },
              "Impossibile eliminare il contatto. Riprova più tardi o contatta l'assistenza tecnica.",
            ),
          ),
        ),
      );
  }

  addContactHistory(contactId: string, dto: AddContactHistoryRequest) {
    return this.httpClient
      .post<{ id: string }>(`${this.baseUrl}/${contactId}/histories`, dto)
      .pipe(
        catchError((error) =>
          throwError(() =>
            apiErrorMessage(error, {
              Generic:
                'Il motivo di chiusura va indicato solo quando lo stato è "Chiusura".',
              NotFound:
                'Il contatto o il motivo di chiusura non sono più disponibili.',
            }),
          ),
        ),
      );
  }

  deleteContactHistory(contactId: string, historyId: string) {
    return this.httpClient.delete<void>(
      `${this.baseUrl}/${contactId}/histories/${historyId}`,
    );
  }

  /**
   * Utenti del portale che il BE propone come possibile collegamento per
   * questo contatto (al massimo dieci, dal più probabile). Il collegamento a
   * email identica lo fa già da sé: qui restano i casi ambigui, che qualcuno
   * deve confermare a mano.
   */
  getContactUserSuggestions(contactId: string) {
    return this.httpClient.get<ContactUserSuggestion[]>(
      `${this.baseUrl}/${contactId}/user-suggestions`,
    );
  }

  linkContactUser(contactId: string, dto: LinkContactUserRequest) {
    return this.httpClient
      .put<void>(`${this.baseUrl}/${contactId}/user`, dto)
      .pipe(
        catchError((error) =>
          throwError(() =>
            apiErrorMessage(error, {
              AlreadyExists:
                "L'utente è già collegato a un altro contatto, oppure la sua email è già usata da un altro contatto.",
              NotFound: "Il contatto o l'utente non esistono più.",
            }),
          ),
        ),
      );
  }

  unlinkContactUser(contactId: string) {
    return this.httpClient.delete<void>(`${this.baseUrl}/${contactId}/user`);
  }
}
